/**
 * Voters — remembering an unfinished import so the same file can pick up where it stopped.
 *
 * The SERVER is the authority on progress (`GET VoterImports/{id}` → `nextChunkIndex`); this store
 * only remembers which batch a file belongs to, keyed by the file's fingerprint. Re-selecting the
 * same file offers "Continue Import #12"; anything else starts a new batch.
 *
 * Per-viewer convenience, so localStorage — and every read and write is guarded: private windows,
 * blocked site data and full quotas must degrade to "no resume offered", never to a thrown error.
 * The shape is validated on read; anything malformed is dropped.
 */

export const RESUME_STORAGE_KEY = 'ielect.voters.importResume.v1';
const MAX_ENTRIES = 10;
/** A batch nobody has touched for a week is not worth offering. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface IResumeEntry {
	fingerprint: string;
	batchId: string;
	number: number;
	fileName: string;
	rowCount: number;
	chunkSize: number;
	/** The column mapping the batch was created with — field → header name. */
	mapping: Record<string, string>;
	savedAt: number;
}

export interface IStorageLike {
	getItem: (key: string) => string | null;
	setItem: (key: string, value: string) => void;
}

const safeStorage = (): IStorageLike | null => {
	try {
		return typeof window !== 'undefined' ? window.localStorage : null;
	} catch {
		return null;
	}
};

const isEntry = (value: unknown): value is IResumeEntry => {
	if (!value || typeof value !== 'object') return false;
	const v = value as Record<string, unknown>;
	return (
		typeof v.fingerprint === 'string' &&
		typeof v.batchId === 'string' &&
		typeof v.number === 'number' &&
		typeof v.fileName === 'string' &&
		typeof v.rowCount === 'number' &&
		typeof v.chunkSize === 'number' &&
		typeof v.savedAt === 'number' &&
		!!v.mapping &&
		typeof v.mapping === 'object'
	);
};

export const readResumeEntries = (storage: IStorageLike | null = safeStorage(), now = Date.now()): IResumeEntry[] => {
	if (!storage) return [];
	try {
		const parsed: unknown = JSON.parse(storage.getItem(RESUME_STORAGE_KEY) ?? '[]');
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(isEntry).filter((e) => now - e.savedAt < MAX_AGE_MS);
	} catch {
		return [];
	}
};

const write = (entries: IResumeEntry[], storage: IStorageLike | null) => {
	if (!storage) return;
	try {
		storage.setItem(RESUME_STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
	} catch {
		// Quota or blocked storage: resume simply won't be offered.
	}
};

export const saveResumeEntry = (entry: IResumeEntry, storage: IStorageLike | null = safeStorage()) => {
	const rest = readResumeEntries(storage, entry.savedAt).filter(
		(e) => e.fingerprint !== entry.fingerprint && e.batchId !== entry.batchId
	);
	write([entry, ...rest], storage);
};

export const findResumeEntry = (fingerprint: string, storage: IStorageLike | null = safeStorage(), now = Date.now()) =>
	fingerprint ? (readResumeEntries(storage, now).find((e) => e.fingerprint === fingerprint) ?? null) : null;

export const clearResumeEntry = (batchId: string, storage: IStorageLike | null = safeStorage()) => {
	const entries = readResumeEntries(storage);
	const next = entries.filter((e) => e.batchId !== batchId);
	if (next.length !== entries.length) write(next, storage);
};

const toHex = (buffer: ArrayBuffer): string => [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('');

/**
 * SHA-256 of the file bytes — the server warns when the same file is imported twice, and the resume
 * store keys on it. Falls back to name+size+mtime where SubtleCrypto is unavailable (an insecure
 * origin), and returns '' for the hash then so the server is not handed a fake one.
 */
export const fingerprintFile = async (
	buffer: ArrayBuffer,
	meta: { name: string; size: number; lastModified: number }
): Promise<{ sha256: string; fingerprint: string }> => {
	try {
		const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
		if (subtle) {
			const sha256 = toHex(await subtle.digest('SHA-256', buffer));
			return { sha256, fingerprint: `sha256:${sha256}` };
		}
	} catch {
		// fall through
	}
	return { sha256: '', fingerprint: `meta:${meta.name}|${meta.size}|${meta.lastModified}` };
};
